import type { IDiscountCampaign } from '@shared/jp/discount/interfaces';
import { calculateDiscount } from '@shared/common/discount/utils';
import {
  getShippingDiscount,
  getDiscount,
  applyShippingDiscount,
} from '@shared/jp/discount/utils';
import type {
  IBookInOrder,
  IBookSimpleData,
  IPaymentInformation,
} from '@shared/jp/interfaces';
import type {
  IProductInOrder,
  TCouponData,
  TWithDocId,
} from '@shared/common/interfaces';
import { Logger } from '@shared/common/logger';
import type { TBookData } from '@shared/jp/models';
import type { PartialUndefined } from '@studiobuki/web-core';
import type { ICalculatorComponentItem } from '@studiobuki/web-core/lib/calculator';
import type StripeBE from 'stripe';
import memoize from 'lodash/memoize';
import type { TShippingMethod } from '@shared/jp/shipping/interfaces';
import type {
  TBookAlias,
  TBookCover,
  TBookWrapping,
  TProductAlias,
  TProductOption,
} from '@shared/jp/product';
import {
  BOOK_ALIAS_TOKYO_METRO,
  getKidstitle,
  HERO_TOKYO_METRO_VALUE_TO_LABEL_MAP,
  isBookTokyoMetroData,
  KUNCHAN_VALUE_TO_LABEL_MAP,
  TOKYO_METRO_KANJI_VALUE_TO_LABEL_MAP,
  BOOK_ALIAS_LETS_MAKE_METRO,
  isBookLetsMakeMetro,
  STORY_LINES_TO_NAME_MAP,
} from '@shared/jp/product';
import type { IShipping } from '@shared/jp/data/shipping';
import type { IBookCover, IPrice } from '@shared/jp/data/products';
import { getHeroName } from '@shared/jp/utils';
import type { IBookCoverCommon } from '@shared/common/data/products';
import jpJP from '@shared/common/translations/ja-JP.json';
import type { TProduct } from '@studiobuki/shared/dist/common/products';
import {
  getProductConfig,
  getProductPrice as _getProductPrice,
} from '@studiobuki/shared/dist/common/products';
import type { IProduct, ISpecs } from './types';
import { getBookByAlias } from './app/data/books/utils';
import { wrappingDefault, wrappings } from './static';

type TJPKey = keyof typeof jpJP;

export const scrollToElement = (
  element: Element,
  container = document.documentElement,
  _yGap: number | null = null,
): void => {
  const { x, y } = element.getBoundingClientRect();
  let yGap = _yGap || 0;

  if (_yGap === null && container === document.documentElement) {
    const styles = getComputedStyle(container);
    const topbarHeight = parseInt(
      styles.getPropertyValue('--buki-topbar-height'),
      10,
    );
    const headerHeight = parseInt(
      styles.getPropertyValue('--buki-header-height'),
      10,
    );

    yGap = topbarHeight + headerHeight;
  }

  container.scrollBy({
    top: y - yGap,
    left: x,
    behavior: 'smooth',
  });
};

const scrollToSelectorLog = new Logger('scrollToSelector');
export const scrollToSelector = (
  selector: string,
  container = document.documentElement,
  _yGap = null,
): void => {
  const log = scrollToSelectorLog;
  const element = document.querySelector(selector);

  if (!element) {
    log.error('element not found by selector', selector);
    return;
  }

  scrollToElement(element, container, _yGap);
};

export const checkStripeCoupon = (coupon: any): coupon is StripeBE.Coupon =>
  coupon && ('percent_off' in coupon || 'amount_off' in coupon);

export const getCouponDiscount = ({
  percent_off,
  amount_off,
}: StripeBE.Coupon): string => {
  if (percent_off) {
    return `${percent_off}%`;
  }
  if (amount_off) {
    return `${amount_off}`;
  }

  return '0';
};

export const getCalculatorItems = (
  products: IProduct[],
  discountCampaign?: IDiscountCampaign,
  couponData?: TCouponData,
  shipping?: IShipping,
): ICalculatorComponentItem[] => {
  const calculatorItems: ICalculatorComponentItem[] = [];

  calculatorItems.push(
    ...products.map((p) => {
      let price = p.cover.price.value + p.wrapping.price.value;

      const discount =
        discountCampaign && getDiscount(discountCampaign, p.alias);

      if (discount) {
        price = calculateDiscount(price, discount);
      }

      if (checkStripeCoupon(couponData)) {
        price = calculateDiscount(price, getCouponDiscount(couponData));
      }

      return {
        name: '商品',
        price,
        currency: p.cover.price.currency,
      };
    }),
  );

  if (shipping) {
    const shippingMethod = shipping.id as TShippingMethod;
    let shippingPrice = shipping.price;

    const shippingDiscount =
      discountCampaign && getShippingDiscount(discountCampaign, shippingMethod);

    if (shippingDiscount) {
      shippingPrice = calculateDiscount(shippingPrice, shippingDiscount);
    }

    calculatorItems.push({
      name: '送料',
      price: shippingPrice,
      currency: shipping.currency,
    });
  }

  return calculatorItems;
};

export const createBookInOrderObject = (
  alias: TBookAlias,
  bookId: string,
  wrapping: TBookWrapping,
  cover: TBookCover,
): IBookInOrder => ({
  alias,
  bookId,
  status: 0,
  wrapping,
  cover,
});

export const FEToBEProduct = ({
  alias,
  bookId,
  wrapping,
  cover,
}: IProduct): IBookInOrder =>
  createBookInOrderObject(alias, bookId, wrapping.id, cover.id);

export const getProductPrice = (p: IProduct): IPrice => ({
  value: p.cover.price.value + p.wrapping.price.value,
  currency: p.cover.price.currency,
});

export const getCovers = memoize((alias: TProductAlias) => {
  const book = getBookByAlias(alias as TBookAlias);

  return book.options;
});

export const getCover = (alias: TProductAlias, cover: TProductOption) => {
  const covers = getCovers(alias);

  return covers[cover as keyof typeof covers] as IBookCoverCommon;
};

const getBookDataSpecsLog = new Logger('getBookDataSpecs');

export const getBookDataSpecs = (
  bookData: TBookData,
  exceptFields: string[] = [],
): ISpecs => {
  const log = getBookDataSpecsLog;

  const { messageText } = bookData;

  const heroName = getHeroName(bookData);

  const specs: ISpecs = {
    /** hero's name */
    名前: heroName,
  };

  if (isBookTokyoMetroData(bookData)) {
    const { kunChan, trainLine, character, glasses } = bookData;

    Object.assign(specs, {
      /** kunChan */
      'くん・ちゃん付け': `${KUNCHAN_VALUE_TO_LABEL_MAP[kunChan]}`,
      /** hero */
      性別: `${HERO_TOKYO_METRO_VALUE_TO_LABEL_MAP[character]}`,
      /** glasses */
      メガネ: glasses ? 'あり' : 'なし',
      /** trainline */
      運転する路線: `${TOKYO_METRO_KANJI_VALUE_TO_LABEL_MAP[trainLine]}`,
      /** photo */
      写真: 'あり',
      /** dedication */
      'メッセージ（左ページ）': messageText,
    });
  } else if (isBookLetsMakeMetro(bookData)) {
    const { kunChan, gender, hairColor, hairStyle, glasses, storyLine } =
      bookData;

    Object.assign(specs, {
      /** kunChan */
      'くん・ちゃん付け': `${KUNCHAN_VALUE_TO_LABEL_MAP[kunChan]}`,
      性別: `${jpJP[gender as TJPKey]}`,
      髪色: `${jpJP[`hair-color.${hairColor}` as TJPKey]}`,
      髪型: `${jpJP[`lmm.hair-style.${hairStyle}` as TJPKey]}`,
      /** glasses */
      メガネ: glasses ? 'あり' : 'なし',
      /** photo */
      写真: 'あり',
      お手伝いする分野: `${STORY_LINES_TO_NAME_MAP[storyLine!]}`,
      /** dedication */
      メッセージ: messageText,
    });
  } else {
    log.error(`didn't recognize`, { bookData });
  }

  // Object.assign(specs, {
  //   /** cover */
  //   表紙の種類: cover ? `「${jpJP[cover]}」` : '',
  // });

  // removing excepted fields
  exceptFields.forEach((field) => delete specs[field]);

  // removing empty fields
  Object.keys(specs).forEach((key) => specs[key] || delete specs[key]);

  return specs;
};

const getProductNameLog = new Logger('getProductName');
export const getProductName = (
  alias: TBookAlias,
  heroName: string,

  // gender?: TGender | TGenderGrandpaGrandma,
  // childrenNames?: string[],
) => {
  const log = getProductNameLog;

  switch (alias) {
    case BOOK_ALIAS_TOKYO_METRO:
      return `うんてんしは${heroName}`;
    case BOOK_ALIAS_LETS_MAKE_METRO:
      return `地下鉄の父・早川徳次と地下鉄の子・${heroName}`;
    default:
      log.error(`alias "${alias}" not handled yet`);
      return '';
  }
};

type TGetProductRequiredFields = 'alias' | 'bookId' | 'hero' | 'specs';

type TGetProductExcludedFields = 'covers' | 'wrappings' | 'name';

type TGetProductFields = Required<Pick<IProduct, TGetProductRequiredFields>> &
  PartialUndefined<
    Omit<IProduct, TGetProductRequiredFields | TGetProductExcludedFields>
  >;

export const getProduct = ({
  alias,
  bookId,
  cover,
  hero,
  children,
  gender,
  image,
  wrapping,
  specs,
}: TGetProductFields): IProduct => {
  const book = getBookByAlias(alias);

  const p: IProduct = {
    alias,
    bookId,
    name: getProductName(alias, hero),
    covers: Object.values(book.options),
    cover: cover || book.options[book.option],
    hero,
    image: image || book.menuPhoto,
    wrappings,
    wrapping: wrapping || wrappingDefault,
    specs,
  };

  if (children) {
    p.children = children;
  }

  if (gender) {
    p.gender = gender;
  }

  return p;
};

export const getProductByBookData = (bookData: TBookData) =>
  getProduct({
    alias: bookData.alias as TBookAlias,
    bookId: bookData.bookId,
    hero:
      getHeroName(bookData) +
      ('kunChan' in bookData ? getKidstitle(bookData.kunChan) : ''),
    gender: 'gender' in bookData ? bookData.gender : undefined,
    children:
      'grandchildren' in bookData
        ? bookData.grandchildren.data.map(({ childrenName }) => childrenName)
        : undefined,
    wrapping: ((wrapping: TBookWrapping | undefined) =>
      wrapping && wrappings.find((w) => w.id === wrapping))(bookData.wrapping),
    cover: getCover(bookData.alias, bookData.cover) as IBookCover,
    specs: getBookDataSpecs(bookData),
  });

const getPaymentInformationLog = new Logger('getPaymentInformation');
export const getPaymentInformation = (
  type: IPaymentInformation['type'],
  cartData: {
    books: IBookSimpleData[];
    products?: IProductInOrder[] | undefined;
  },
  /** discount is not calculated for `number` */
  shippingData:
    | {
        shippingPrice: number;
        shippingMethod: TShippingMethod;
      }
    | number,
  configData:
    | {
        productsConfig?: Record<string, TWithDocId<TProduct>> | undefined;
        discountCampaign?: IDiscountCampaign | undefined;
      }
    | undefined,
  couponData?: TCouponData | undefined,
  additionalPrice = 0,
) => {
  const log = getPaymentInformationLog;
  const discountCampaign = configData?.discountCampaign;
  const productsConfig = configData?.productsConfig;
  const { books, products } = cartData;

  /** raw books price */
  const productSubtotal: IPaymentInformation['productSubtotal'] = books.reduce(
    (totalPrice, { alias, cover, count: _count }) => {
      const count = _count || 1;
      let bookPrice = getCover(alias, cover).price.value * count;

      const discount = discountCampaign && getDiscount(discountCampaign, alias);

      if (discount) bookPrice = calculateDiscount(bookPrice, discount);

      return totalPrice + bookPrice + additionalPrice * count;
    },
    products?.reduce((totalPrice, productInOrder) => {
      const userProduct = productInOrder.product;
      const alias = userProduct.productId;
      const productConfig = productsConfig?.[alias];

      if (!productConfig) {
        log.error('product config not found by productId', {
          productsConfig,
          productInOrder,
        });
        return totalPrice;
      }

      const config = getProductConfig(
        productConfig.config,
        userProduct.options,
      );

      let productPrice = _getProductPrice(
        config,
        productConfig.steps,
        userProduct.activeSectionsMap,
      );

      // apply discount campaign
      const discount = discountCampaign && getDiscount(discountCampaign, alias);

      if (discount) productPrice = calculateDiscount(productPrice, discount);

      return totalPrice + productPrice + additionalPrice;
    }, 0) || 0,
  );

  /** final books price */
  let productTotal = productSubtotal;
  let couponCode: IPaymentInformation['couponCode'];

  // apply stripe coupon
  if (checkStripeCoupon(couponData)) {
    productTotal = calculateDiscount(
      productTotal,
      getCouponDiscount(couponData),
    );
    couponCode = couponData.id;
  }

  // define shipping price
  let shippingPrice = 0;

  if (typeof shippingData === 'number') {
    shippingPrice = shippingData;
  } else {
    shippingPrice = discountCampaign
      ? applyShippingDiscount(
          shippingData.shippingPrice,
          shippingData.shippingMethod,
          discountCampaign,
        )
      : shippingData.shippingPrice;
  }

  const res: IPaymentInformation = {
    discountAmount: productSubtotal - productTotal,
    productSubtotal,
    productTotal,
    shippingPrice,
    orderTotal: productTotal + shippingPrice,
    type,
    paymentMethod: 'Card',
    paymentIntent: 'undefined',
  };

  if (couponCode) {
    res.couponCode = couponCode;
  }

  return res;
};

/**
 * Checks if a specific parameter has changed between two objects.
 * @param _a The first object to compare.
 * @param _b The second object to compare.
 * @param param The parameter to check for changes.
 * @returns `true` if the parameter has changed, `false` otherwise.
 */
export const isParamChanged = <T extends { [key: string]: any }>(
  _a: T | null | undefined | false,
  _b: T | null | undefined | false,
  param: string,
) => {
  const a = _a && param in _a ? _a[param as keyof T] : null;
  const b = _b && param in _b ? _b[param as keyof T] : null;

  return a !== b;
};
